import { addDays } from 'date-fns';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { updateFilterValues } from '~/components/AnalyzerPage/AnalyzerPage.utils';
import DeprecatedUserMultiSelect from '~/components/Client/DeprecatedUserMultiSelect';
import { flattenFolderTree } from '~/components/SearchV2/SearchV2.utils';
import { getFolderTree } from '~/components/UploadPage/UploadPage.data';
import { Button } from '~/eds/src/components/Button/Button';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { parseDate } from '~/eds/src/utils/parseDate';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';

import { black5, evisortBlue } from '../../../assets/shared-styles/general';
import {
  MODAL_DEPARTMENT_TREE,
  MODAL_FOLDER_TREE,
} from '../../../types/modal.types';
import { ERROR } from '../../../types/toast.types';
import { togglePinFilterField } from '../../AlertsPage/AlertsPage.data';
import EcCheckbox from '../../Shared/EcCheckbox/EcCheckbox';
import EcDatepicker from '../../Shared/EcDatepicker/EcDatepicker';
import EcFiltersSection from '../../Shared/EcFiltersSection/EcFiltersSection';
import EcInput from '../../Shared/EcInput/EcInput';
import EcModal from '../../Shared/EcModal/EcModal';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import EcMultipleSelectValue from '../../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcMultiSelect from '../../Shared/EcMultiSelect/EcMultiSelect';
import EcSelect from '../../Shared/EcSelect/EcSelect';
import { showToast } from '../../Shared/EcToast/showToast';
import CheckmarkIcon from '../../Shared/Icons/CheckmarkIcon';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import PlusIcon from '../../Shared/Icons/PlusIcon';
import SubArrowIcon from '../../Shared/Icons/SubArrowIcon';
import { updateFilterPinnedProperty } from '../AddFilterModal/AddFilterModal.utils';
import { getAlertModalData } from './AlertModal.data';
import { getOptions } from './AlertModal.options';
import styles from './AlertModal.styles';
import {
  createFormattedDateString,
  processData,
  validateFiltersForm,
} from './AlertModal.utils';

class AlertModal extends Component {
  constructor(props) {
    super(props);

    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleFoldersChange = this.handleFoldersChange.bind(this);
    this.handleDepartmentsChange = this.handleDepartmentsChange.bind(this);
    this.handleCreateAlert = this.handleCreateAlert.bind(this);
    this.handleUpdateAlert = this.handleUpdateAlert.bind(this);
    this.handleFilterOptionsChange = this.handleFilterOptionsChange.bind(this);
    this.handleSubmitFilters = this.handleSubmitFilters.bind(this);

    const { intl } = this.props;
    const options = getOptions(intl);

    this.state = {
      loading: false,
      currentModal: null,
      userFieldsSuggestions: [],
      editMode: !!this.props.alertId && !this.props.isDuplicated,
      step: this.props.step,
      alertData: null,
      active: this.props.active,
      name: this.props.name,
      message: this.props.message,
      occurrenceInterval: this.props.occurrenceInterval,
      occurrencePeriod:
        this.props.occurrencePeriod ||
        options.OCCURRENCE_PERIOD_OPTIONS.find((obj) => obj.value === 'Hour'),
      startDate: this.props.startDate,
      occurrenceEndTimespan:
        this.props.occurrenceEndTimespan ||
        options.OCCURRENCE_END_TIMESPAN_OPTIONS.find(
          (obj) => obj.value === 'never',
        ),
      occurrenceEndDate: this.props.occurrenceEndDate,
      occurrenceEndLimit: this.props.occurrenceEndLimit,
      folderTree: {},
      folders: this.props.folders,
      filters: this.props.filters,
      filterOptions: [],
      provisionOptions: [],
      users: this.props.users.map((user) => user.id),
      userFields: this.props.userFields,
      excludedUsers: this.props.excludedUsers.map((user) => user.id),
      departments: this.props.departments,
      editAccess: this.props.editAccess,
    };
  }

  componentDidMount() {
    const { currentUser, alertId } = this.props;

    this.setState({ loading: true });
    Promise.all([
      getFolderTree(),
      getAlertModalData(currentUser.client, alertId),
    ])
      .then(([folderTreeData, alertModalData]) => {
        const folderTreeArray = flattenFolderTree(
          folderTreeData?.children ?? [],
        );
        const folderTree = folderTreeArray.reduce((acc, folder) => {
          acc[folder.value] = folder;
          return acc;
        }, {});
        const { customFieldsRes, sections, provisionOptions, alertData } =
          alertModalData;
        const newSections = sections.sections.map((sec) =>
          sec.title === 'Other Fields' ? { ...sec } : sec,
        );

        this.setState({
          folderTree,
          userFieldsSuggestions: this.setUserFieldFormat(customFieldsRes),
          filterOptions: newSections,
          provisionOptions: provisionOptions.suggestions,
        });
        if (alertId) {
          this.setAlertData(alertData, folderTree, newSections);
        }
      })
      .catch(() => {
        showToast(
          ERROR,
          this.props.intl.formatMessage({
            id: 'AlertModal.somethingWentWrong',
            defaultMessage:
              'Something went wrong with fetching alert modal options.',
            description: 'Error message when fetching alert modal options',
          }),
        );
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  setUserDataFormat(usersList) {
    return usersList.map((user) => user.id);
  }

  setUserFieldFormat(userFields) {
    return userFields.map(({ id, label }) => {
      return { id, label, name: label, value: label };
    });
  }

  setAlertData(res, folderTree, filterOptions) {
    const { isDuplicated, intl } = this.props;
    const { OCCURRENCE_END_TIMESPAN_OPTIONS, OCCURRENCE_PERIOD_OPTIONS } =
      getOptions(intl);

    let occurrenceEndTimespan = OCCURRENCE_END_TIMESPAN_OPTIONS.find(
      (obj) => obj.value === 'never',
    );
    if (res.recurrences) {
      occurrenceEndTimespan = OCCURRENCE_END_TIMESPAN_OPTIONS.find(
        (obj) => obj.value === 'number_of_occurrences',
      );
    } else if (res.end_date) {
      occurrenceEndTimespan = OCCURRENCE_END_TIMESPAN_OPTIONS.find(
        (obj) => obj.value === 'on_a_specific_date',
      );
    }
    const flattenedSections = filterOptions.reduce((acc, val) => {
      return acc.concat(val.fields);
    }, []);
    const filterQuery = res.query || this.props.filters;
    const mappedFiltersWithFieldValues = updateFilterValues(
      flattenedSections,
      filterQuery,
    );

    const parsedFolderList = res.folders.map((folder) => ({
      id: folder.id,
      name: folderTree[folder.id]?.display_value ?? 'Restricted',
    }));

    this.setState({
      alertData: res,
      active: res.active || this.props.active,
      name: res.name || this.props.name,
      message: res.message || this.props.message,
      occurrenceInterval:
        res.occurrence_interval || this.props.occurrenceInterval,
      occurrencePeriod:
        OCCURRENCE_PERIOD_OPTIONS.find(
          (option) => option.value === res.occurrence_period,
        ) || this.props.occurrencePeriod,
      startDate: isDuplicated
        ? this.props.startDate
        : res.start_date
          ? parseDate(res.start_date)
          : this.props.startDate,
      occurrenceEndTimespan: isDuplicated
        ? this.props.occurrenceEndTimespan
        : occurrenceEndTimespan,
      occurrenceEndDate: isDuplicated
        ? this.props.occurrenceEndDate
        : res.end_date
          ? parseDate(res.end_date)
          : this.props.occurrenceEndDate,
      occurrenceEndLimit: res.recurrences || this.props.occurrenceEndLimit,
      folders: parsedFolderList || this.props.folders,
      filters: mappedFiltersWithFieldValues,
      users: res.users ? this.setUserDataFormat(res.users) : this.props.users,
      userFields: res.user_fields
        ? this.setUserFieldFormat(res.user_fields)
        : this.props.userFields,
      excludedUsers: res.excluded_users
        ? this.setUserDataFormat(res.excluded_users)
        : this.props.excludedUsers,
      departments: res.departments || this.props.departments,
      editAccess: res.edit_access || this.props.editAccess,
    });
  }

  handleShowModal(currentModal) {
    this.setState({ currentModal });
  }

  handleHideModal() {
    this.setState({ currentModal: null });
  }

  onCalendarOpenClose = (open) => {
    this.props.disableEscapeKey(open);
  };

  handleCreateAlert() {
    const { handleAlertAction, hideModal } = this.props;
    const { step: _step, ...data } = this.state;

    const processedData = processData(data);

    handleAlertAction(processedData);
    hideModal();
  }

  handleUpdateAlert() {
    const { handleAlertAction, hideModal, alertId } = this.props;
    const { step: _step, ...data } = this.state;
    const { alertData } = this.state;

    const processedData = processData(data);

    const updatedKeys = Object.keys(processedData)
      .map((key) => {
        const initialValue = alertData[key];
        const currentValue = processedData[key];

        if ((key === 'start_date' || key === 'end_date') && initialValue) {
          if (createFormattedDateString(initialValue) !== currentValue)
            return key;
        } else if (Array.isArray(initialValue)) {
          if (key === 'departments') {
            if (
              initialValue.map((val) => val.department_id).toString() !==
              currentValue.toString()
            )
              return key;
          } else {
            if (
              initialValue.map((val) => val.id).toString() !==
              currentValue.toString()
            )
              return key;
          }
        } else if (
          !Array.isArray(initialValue) &&
          initialValue !== currentValue
        ) {
          return key;
        }
        return null;
      })
      .filter((el) => el);

    const patchData = {};
    updatedKeys.forEach((key) => (patchData[key] = processedData[key]));

    handleAlertAction(alertId, patchData);
    hideModal();
  }

  handleFoldersChange(locations) {
    const newFolders = [...this.state.folders];
    newFolders.push(locations);

    this.setState({ folders: newFolders.flat(Infinity) });
  }

  handleDepartmentsChange(departments) {
    this.setState({ departments });
  }

  updateFilterOptionUI(filterId) {
    const { filterOptions } = this.state;

    const newFilterOptions = updateFilterPinnedProperty(
      filterId,
      filterOptions,
    );
    this.setState({ filterOptions: newFilterOptions });
  }

  handleFilterOptionsChange(filterId) {
    this.updateFilterOptionUI(filterId);

    togglePinFilterField(filterId).catch(() => {
      showToast(
        ERROR,
        this.props.intl.formatMessage({
          id: 'AlertModal.pinFilterError',
          defaultMessage: "There's been an error with pinning this filter.",
          description: 'Error message when pinning a filter fails',
        }),
      );
      this.updateFilterOptionUI(filterId);
    });
  }

  renderSelectDepartmentTreeModal() {
    const { currentUser } = this.props;
    const { departments } = this.state;

    return (
      <EcModal
        modalType={MODAL_DEPARTMENT_TREE}
        width="540px"
        client={currentUser.client}
        title={this.props.intl.formatMessage({
          id: 'AlertModal.selectADepartment',
          defaultMessage: 'Select a Department',
          description: 'Title for the select department modal',
        })}
        departmentsSelected={departments}
        handleDepartmentChange={this.handleDepartmentsChange}
        confirmButtonIcon={<CheckmarkIcon size="20" />}
        confirmButtonText={this.props.intl.formatMessage({
          id: 'AlertModal.selectDepartment',
          defaultMessage: 'Select Department',
          description: 'Label for the select department button',
        })}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderFirstStep() {
    const { classes, flags, hideModal, intl, formatDate } = this.props;
    const { OCCURRENCE_PERIOD_OPTIONS, OCCURRENCE_END_TIMESPAN_OPTIONS } =
      getOptions(intl);
    const {
      loading,
      editMode,
      name,
      message,
      occurrenceInterval,
      occurrencePeriod,
      startDate,
      occurrenceEndTimespan,
      occurrenceEndDate,
      occurrenceEndLimit,
      folders,
    } = this.state;

    const enableLocalization = flags[FlagType.LanguageSelect];
    return (
      <EcModalCard
        title={
          editMode
            ? intl.formatMessage({
                id: 'AlertModal.editAlertBasicDetails',
                defaultMessage: 'Edit Alert → Basic Details',
                description: 'Edit alert modal title',
              })
            : intl.formatMessage({
                id: 'AlertModal.addAlertBasicDetails',
                defaultMessage: 'Add Alert → Basic Details (Step 1/3)',
                description: 'Add alert modal title step 1',
              })
        }
        content={
          loading ? (
            <div className={classes.loadingContainer}>
              <LoadingSpinner size="medium" />
            </div>
          ) : (
            <>
              <div className={classes.nameSection}>
                <div className={classes.optionLabel}>
                  {intl.formatMessage({
                    id: 'AlertModal.nameLabel',
                    defaultMessage: 'NAME',
                    description: 'Name field label',
                  })}
                </div>
                <EcInput
                  width="100%"
                  input={{
                    value: name,
                    onChange: (event) =>
                      this.setState({ name: event.target.value }),
                  }}
                />
              </div>
              <div className={classes.messageSection}>
                <div className={classes.optionLabel}>
                  {intl.formatMessage({
                    id: 'AlertModal.messageLabel',
                    defaultMessage: 'MESSAGE',
                    description: 'Message field label',
                  })}
                </div>
                <textarea
                  className={classes.optionTextArea}
                  rows="6"
                  value={message}
                  onChange={(e) => this.setState({ message: e.target.value })}
                />
              </div>
              <div className={classes.schedulingSection}>
                <div className={classes.schedulingLabel}>
                  {intl.formatMessage({
                    id: 'AlertModal.schedulingLabel',
                    defaultMessage: 'Scheduling',
                    description: 'Scheduling section label',
                  })}
                </div>
                <div className={classes.schedulingSubSections}>
                  <div className={classes.frequencySection}>
                    <div className={classes.optionLabel}>
                      {intl.formatMessage({
                        id: 'AlertModal.frequencyLabel',
                        defaultMessage: 'FREQUENCY',
                        description: 'Frequency field label',
                      })}
                    </div>
                    <div className={classes.optionInlineFields}>
                      <EcInput
                        width="80px"
                        type="number"
                        min="1"
                        step="1"
                        input={{
                          value: occurrenceInterval,
                          onChange: (event) =>
                            this.setState({
                              occurrenceInterval: event.target.value,
                            }),
                        }}
                      />
                      <div className={classes.optionFieldsSeparator} />
                      <EcSelect
                        width="120px"
                        options={OCCURRENCE_PERIOD_OPTIONS}
                        value={occurrencePeriod}
                        onChange={(option) =>
                          this.setState({ occurrencePeriod: option })
                        }
                      />
                    </div>
                  </div>
                  <div className={classes.startDateSection}>
                    <div className={classes.optionLabel}>
                      {intl.formatMessage({
                        id: 'AlertModal.startDateLabel',
                        defaultMessage: 'START DATE',
                        description: 'Start date field label',
                      })}
                    </div>
                    <div className={classes.optionDatepicker}>
                      {enableLocalization ? (
                        <DateInput
                          max={
                            occurrenceEndDate
                              ? parseDate(occurrenceEndDate)
                              : null
                          }
                          value={startDate ? parseDate(startDate) : null}
                          onChange={(date) => {
                            this.setState({
                              startDate: date
                                ? formatDate(date, 'iso')
                                : formatDate(new Date(), 'iso'),
                            });
                          }}
                        />
                      ) : (
                        <EcDatepicker
                          date={startDate ? parseDate(startDate) : null}
                          isInModal={true}
                          onDateChange={(date) => {
                            this.setState({
                              startDate: date
                                ? formatDate(date, 'iso')
                                : formatDate(new Date(), 'iso'),
                            });
                          }}
                          onCalendarOpenClose={this.onCalendarOpenClose}
                        />
                      )}
                    </div>
                  </div>
                  <div className={classes.endsSection}>
                    <div className={classes.optionLabel}>
                      {intl.formatMessage({
                        id: 'AlertModal.endsLabel',
                        defaultMessage: 'ENDS',
                        description: 'Ends field label',
                      })}
                    </div>
                    <div className={classes.optionInlineFields}>
                      <EcSelect
                        width="216px"
                        options={OCCURRENCE_END_TIMESPAN_OPTIONS}
                        value={occurrenceEndTimespan}
                        onChange={(option) => {
                          if (option.value === 'on_a_specific_date') {
                            this.setState({
                              occurrenceEndDate: addDays(
                                parseDate(startDate),
                                1,
                              ),
                              occurrenceEndLimit: '',
                            });
                          } else if (option.value === 'number_of_occurrences') {
                            this.setState({ occurrenceEndDate: null });
                          } else {
                            this.setState({
                              occurrenceEndDate: null,
                              occurrenceEndLimit: '',
                            });
                          }
                          this.setState({ occurrenceEndTimespan: option });
                        }}
                      />
                      <div className={classes.optionFieldsSeparator} />
                      {occurrenceEndTimespan?.value === 'never' && null}
                      {occurrenceEndTimespan?.value ===
                        'on_a_specific_date' && (
                        <div className={classes.optionDatepicker}>
                          {enableLocalization ? (
                            <DateInput
                              min={addDays(parseDate(startDate), 1)}
                              value={
                                occurrenceEndDate
                                  ? parseDate(occurrenceEndDate)
                                  : addDays(parseDate(startDate), 1)
                              }
                              onChange={(date) => {
                                this.setState({
                                  occurrenceEndDate: date
                                    ? formatDate(date, 'iso')
                                    : null,
                                });
                              }}
                            />
                          ) : (
                            <EcDatepicker
                              date={
                                occurrenceEndDate
                                  ? parseDate(occurrenceEndDate)
                                  : addDays(parseDate(startDate), 1)
                              }
                              isInModal={true}
                              minDate={addDays(parseDate(startDate), 1)}
                              onDateChange={(date) => {
                                this.setState({
                                  occurrenceEndDate: date
                                    ? formatDate(date, 'iso')
                                    : null,
                                });
                              }}
                              onCalendarOpenClose={this.onCalendarOpenClose}
                            />
                          )}
                        </div>
                      )}
                      {occurrenceEndTimespan?.value ===
                        'number_of_occurrences' && (
                        <EcInput
                          width="80px"
                          type="number"
                          min="1"
                          step="1"
                          input={{
                            value: occurrenceEndLimit,
                            onChange: (event) =>
                              this.setState({
                                occurrenceEndLimit: event.target.value,
                              }),
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.foldersSection}>
                <div className={classes.foldersLabel}>
                  {intl.formatMessage({
                    id: 'AlertModal.foldersLabel',
                    defaultMessage: 'Folders',
                    description: 'Folders section label',
                  })}
                </div>
                <div className={classes.optionsInfo}>
                  {intl.formatMessage({
                    id: 'AlertModal.foldersInfo',
                    defaultMessage:
                      'You can specify one or more folders to limit the documents being tracked for changes. If no folders are specified, all documents will be tracked.',
                    description: 'Folders section information',
                  })}
                </div>
                <div className={classes.foldersSelected}>
                  {folders.length ? (
                    <div className={classes.selectedFolders}>
                      {folders.map((folder, index) => (
                        <EcMultipleSelectValue
                          key={`folder-${index}`}
                          label={folder.name}
                          closeHandler={() => {
                            const newFolders = [...this.state.folders].filter(
                              (f) => f.id !== folder.id,
                            );

                            this.setState({ folders: newFolders });
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={classes.selectedFoldersEmpty}>
                      {intl.formatMessage({
                        id: 'AlertModal.noFoldersSelected',
                        defaultMessage: "You haven't selected any folders.",
                        description: 'No folders selected message',
                      })}
                    </div>
                  )}
                  <Button
                    onClick={() => this.handleShowModal(MODAL_FOLDER_TREE)}
                    variant="action"
                    text={intl.formatMessage({
                      id: 'AlertModal.addFolderButton',
                      defaultMessage: 'Add a Folder',
                      description: 'Add folder button text',
                    })}
                    icon="plus"
                    iconPosition="left"
                  />
                </div>
              </div>
            </>
          )
        }
        footer={
          <>
            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'AlertModal.cancelFirstStep',
                defaultMessage: 'Cancel',
                description: 'Cancel button text for first step in Alert Modal',
              })}
              onClick={hideModal}
            />
            {editMode ? (
              <Button
                text={intl.formatMessage({
                  id: 'AlertModal.saveFirstStep',
                  defaultMessage: 'Save Alert',
                  description:
                    'Save alert button text for first step in Alert Modal',
                })}
                variant="primary"
                disabled={!name || !message}
                onClick={this.handleUpdateAlert}
              />
            ) : (
              <Button
                text={intl.formatMessage({
                  id: 'AlertModal.nextStepFiltersButton',
                  defaultMessage: 'Next Step: Filters',
                  description: 'Next step filters button text',
                })}
                variant="primary"
                disabled={
                  !(name && name.trim()) ||
                  !(message && message.trim()) ||
                  !occurrenceInterval
                }
                onClick={() => this.setState({ step: 2 })}
              />
            )}
          </>
        }
        hideModal={hideModal}
      />
    );
  }

  renderSecondStep() {
    const { classes, hideModal, isDuplicated, intl } = this.props;
    const { loading, editMode, filters, filterOptions, provisionOptions } =
      this.state;

    return (
      <EcModalCard
        title={
          editMode
            ? intl.formatMessage({
                id: 'AlertModal.editAlertFilters',
                defaultMessage: 'Edit Alert → Filters',
                description: 'Edit alert filters title',
              })
            : intl.formatMessage({
                id: 'AlertModal.addAlertFilters',
                defaultMessage: 'Add Alert → Filters (Step 2/3)',
                description: 'Add alert filters title',
              })
        }
        content={
          <>
            {loading && (editMode || isDuplicated) ? (
              <div className={classes.loadingContainer}>
                <LoadingSpinner size="medium" />
              </div>
            ) : (
              <>
                <div className={classes.filtersSection}>
                  <div className={classes.filtersLabel}>
                    {intl.formatMessage({
                      id: 'AlertModal.filtersLabel',
                      defaultMessage: 'Filters',
                      description: 'Filters section label',
                    })}
                  </div>
                  <EcFiltersSection
                    filters={filters}
                    filterOptions={filterOptions}
                    provisionOptions={provisionOptions}
                    onChange={(selectedFilters) =>
                      this.setState({ filters: selectedFilters })
                    }
                    handleFilterOptionsChange={this.handleFilterOptionsChange}
                    rootSection
                  />
                </div>
              </>
            )}
          </>
        }
        footer={
          <>
            {editMode ? (
              <Fragment>
                <Button
                  text={intl.formatMessage({
                    id: 'AlertModal.cancelSecondStep',
                    defaultMessage: 'Cancel',
                    description:
                      'Cancel button text for second step in Alert Modal',
                  })}
                  onClick={hideModal}
                  variant="tertiary"
                />
                <Button
                  text={intl.formatMessage({
                    id: 'AlertModal.saveSecondStep',
                    defaultMessage: 'Save Alert',
                    description:
                      'Save alert button text for second step in Alert Modal',
                  })}
                  onClick={this.handleSubmitFilters}
                  variant="primary"
                />
              </Fragment>
            ) : (
              <Fragment>
                <Button
                  text={intl.formatMessage({
                    id: 'AlertModal.backButton',
                    defaultMessage: 'Back',
                    description: 'Back button text',
                  })}
                  variant="tertiary"
                  onClick={() => this.setState({ step: 1 })}
                />
                <Button
                  text={intl.formatMessage({
                    id: 'AlertModal.nextStepRecipientsButton',
                    defaultMessage: 'Next Step: Recipients',
                    description: 'Next step recipients button text',
                  })}
                  variant="primary"
                  onClick={this.handleSubmitFilters}
                />
              </Fragment>
            )}
          </>
        }
        hideModal={hideModal}
        disableEscapeKey={this.state.disableEscapeCloseModal}
      />
    );
  }

  renderThirdStep() {
    const { classes, hideModal, isDuplicated } = this.props;
    const {
      loading,
      editMode,
      users,
      userFields,
      departments,
      excludedUsers,
      editAccess,
      userFieldsSuggestions,
    } = this.state;

    return (
      <EcModalCard
        title={
          editMode
            ? this.props.intl.formatMessage({
                id: 'AlertModal.editAlertRecipients',
                defaultMessage: 'Edit Alert → Recipients',
                description: 'Title for editing alert recipients',
              })
            : this.props.intl.formatMessage({
                id: 'AlertModal.addAlertRecipients',
                defaultMessage: 'Add Alert → Recipients (Step 3/3)',
                description: 'Title for adding alert recipients',
              })
        }
        content={
          <>
            {loading && (editMode || isDuplicated) ? (
              <div className={classes.loadingContainer}>
                <LoadingSpinner size="medium" />
              </div>
            ) : (
              <>
                <div className={classes.recipientsSection}>
                  <div className={classes.recipientsLabel}>
                    {this.props.intl.formatMessage({
                      id: 'AlertModal.recipientsLabel',
                      defaultMessage: 'Recipients',
                      description: 'Recipients section label',
                    })}
                  </div>
                  <div className={classes.recipientsSubsections}>
                    <div className={classes.recipientsSectionContainer}>
                      <div className={classes.optionLabel}>
                        {this.props.intl.formatMessage({
                          id: 'AlertModal.usersLabel',
                          defaultMessage: 'Users',
                          description: 'Users section label',
                        })}
                      </div>

                      <DeprecatedUserMultiSelect
                        width="280px"
                        selectedUserIds={users}
                        onChange={(selectedUserIds) => {
                          this.setState({ users: selectedUserIds });
                        }}
                      />
                    </div>

                    <div className={classes.recipientsSectionContainer}>
                      <div className={classes.optionLabel}>FIELDS</div>
                      <EcMultiSelect
                        value={userFields}
                        options={userFieldsSuggestions}
                        onChange={(selectedFields) =>
                          this.setState({ userFields: selectedFields })
                        }
                        getOptionLabel={(option) => option.label}
                        width="280px"
                        placeholder={this.props.intl.formatMessage({
                          id: 'AlertModal.selectFieldsPlaceholder',
                          defaultMessage: 'Search for fields...',
                          description:
                            'Placeholder for selecting fields in alert modal',
                        })}
                        noOptionsMessage={this.props.intl.formatMessage({
                          id: 'AlertModal.noFieldsFound',
                          defaultMessage: 'No fields found',
                          description:
                            'Message when no fields are found in the dropdown',
                        })}
                      />
                      <div className={classes.multipleValuesContainer}>
                        <SubArrowIcon color={black5} />
                        <div className={classes.multipleValues}>
                          {userFields && userFields.length ? (
                            userFields.map((field) => (
                              <EcMultipleSelectValue
                                label={field.label}
                                key={field.label}
                                closeHandler={() => {
                                  const newCustomFields = [
                                    ...userFields,
                                  ].filter((f) => f.label !== field.label);
                                  this.setState({
                                    userFields: newCustomFields,
                                  });
                                }}
                              />
                            ))
                          ) : (
                            <span className={classes.multipleValuesEmpty}>
                              {this.props.intl.formatMessage({
                                id: 'AlertModal.noFieldsSelected',
                                defaultMessage: 'No Fields selected...',
                                description:
                                  'Message displayed when no fields are selected',
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={classes.recipientsSectionContainer}>
                      <div className={classes.optionLabel}>
                        {this.props.intl.formatMessage({
                          id: 'AlertModal.departmentsLabel',
                          defaultMessage: 'Departments',
                          description: 'Departments section label',
                        })}
                      </div>
                      {departments.length ? (
                        <div className={classes.selectedDepartments}>
                          {departments.map((department, index) => (
                            <EcMultipleSelectValue
                              key={`department-${index}`}
                              label={department.department_name}
                              closeHandler={() => {
                                const newDepartments = [...departments].filter(
                                  (d) =>
                                    d.department_id !==
                                    department.department_id,
                                );
                                this.setState({ departments: newDepartments });
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className={classes.departmentsEmptyState}>
                          {this.props.intl.formatMessage({
                            id: 'AlertModal.noDepartmentsSelected',
                            defaultMessage:
                              'You haven’t selected any departments.',
                            description:
                              'Message displayed when no departments are selected',
                          })}
                        </div>
                      )}
                      <button
                        aria-haspopup="true"
                        className={classes.departmentsAddLink}
                        onClick={() =>
                          this.handleShowModal(MODAL_DEPARTMENT_TREE)
                        }
                      >
                        <PlusIcon size="20" color={evisortBlue} />
                        <span>
                          {this.props.intl.formatMessage({
                            id: 'AlertModal.addDepartment',
                            defaultMessage: 'Add a Department',
                            description: 'Label for the add department button',
                          })}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className={classes.excludeUsersSection}>
                  <div className={classes.excludeUsersLabel}>
                    {this.props.intl.formatMessage({
                      id: 'AlertModal.excludeUsersLabel',
                      defaultMessage: 'Exclude Users',
                      description: 'Label for the exclude users section',
                    })}
                  </div>
                  <div className={classes.optionsInfo}>
                    {this.props.intl.formatMessage({
                      id: 'AlertModal.excludeUsersInfo',
                      defaultMessage:
                        ' You can exclude users from receiving this alert (i.e. if they are part of a department and would automatically receive it otherwise).',
                      description:
                        'Information about excluding users from alert',
                    })}
                  </div>

                  <DeprecatedUserMultiSelect
                    width="280px"
                    selectedUserIds={excludedUsers}
                    onChange={(selectedUserIds) => {
                      this.setState({ excludedUsers: selectedUserIds });
                    }}
                  />
                </div>
                <div className={classes.permissionsSection}>
                  <div className={classes.permissionsLabel}>
                    {this.props.intl.formatMessage({
                      id: 'AlertModal.permissionsLabel',
                      defaultMessage: 'Permissions',
                      description: 'Label for the permissions section',
                    })}
                  </div>
                  <div className={classes.optionsInfo}>
                    {this.props.intl.formatMessage({
                      id: 'AlertModal.permissionsInfo',
                      defaultMessage:
                        ' By default, only you and admins will be able to edit the alert.',
                      description: 'Information about alert permissions',
                    })}
                  </div>
                  <div className={classes.permissionsCheckbox}>
                    <EcCheckbox
                      id="allow-recipient-edit-alert"
                      checked={editAccess}
                      onClick={() =>
                        this.setState((prevState) => ({
                          editAccess: !prevState.editAccess,
                        }))
                      }
                    />
                    <label
                      className={classes.checkboxLabel}
                      htmlFor="allow-recipient-edit-alert"
                    >
                      {this.props.intl.formatMessage({
                        id: 'AlertModal.allowRecipientEdit',
                        defaultMessage:
                          'Allow any recipient to edit this alert',
                        description:
                          'Label for the allow recipient edit checkbox',
                      })}
                    </label>
                  </div>
                </div>
              </>
            )}
          </>
        }
        footer={
          <>
            {editMode ? (
              <Fragment>
                <Button
                  text={this.props.intl.formatMessage({
                    id: 'AlertModal.cancelThirdStep',
                    defaultMessage: 'Cancel',
                    description:
                      'Cancel button text for third step in Alert Modal',
                  })}
                  variant="tertiary"
                  onClick={hideModal}
                />
                <Button
                  text={this.props.intl.formatMessage({
                    id: 'AlertModal.saveAlert',
                    defaultMessage: 'Save Alert',
                    description:
                      'Save alert button text for third step in Alert Modal',
                  })}
                  variant="primary"
                  onClick={this.handleUpdateAlert}
                />
              </Fragment>
            ) : (
              <Fragment>
                <Button
                  text={this.props.intl.formatMessage({
                    id: 'AlertModal.backToFilters',
                    defaultMessage: 'Back to Filters',
                    description:
                      'Back button text for third step in Alert Modal',
                  })}
                  variant="tertiary"
                  onClick={() => this.setState({ step: 2 })}
                />
                <Button
                  text={this.props.intl.formatMessage({
                    id: 'AlertModal.addAlert',
                    defaultMessage: 'Add Alert',
                    description: 'Label for the add alert button',
                  })}
                  onClick={this.handleCreateAlert}
                  variant="primary"
                />
              </Fragment>
            )}
          </>
        }
        hideModal={hideModal}
      />
    );
  }

  handleSubmitFilters() {
    const { filters, editMode } = this.state;
    const { intl } = this.props;
    const { filtersFormValid, validatedFilters } = validateFiltersForm(filters);

    this.setState({ filters: validatedFilters }, () => {
      if (filtersFormValid) {
        if (editMode) {
          this.handleUpdateAlert();
        } else {
          this.setState({ step: 3 });
        }
      } else {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AlertModal.filtersValidationError',
            defaultMessage:
              'Please make sure that none of the selected filters are blank or not set.',
            description: 'Filters validation error message',
          }),
        );
      }
    });
  }

  renderMultiSelectFolderTreeModal() {
    const { folders } = this.state;

    const folderIdsSelected = folders.map((folder) => folder.id);

    return (
      <EcModal
        modalType={MODAL_FOLDER_TREE}
        width="540px"
        title={this.props.intl.formatMessage({
          id: 'AlertModal.chooseFoldersTo',
          defaultMessage: 'Choose Folders to Search',
          description: 'Title for the choose folders modal',
        })}
        folderIdsSelected={folderIdsSelected}
        handleChooseMultipleLocationsClick={this.handleFoldersChange}
        confirmButtonIcon={<CheckmarkIcon size="20" />}
        confirmButtonText={this.props.intl.formatMessage({
          id: 'AlertModal.chooseFolders',
          defaultMessage: 'Choose Folders',
          description: 'Label for the choose folders button',
        })}
        hideModal={this.handleHideModal}
        isMultiSelect={true}
      />
    );
  }

  render() {
    const { step, currentModal } = this.state;

    return (
      <Fragment>
        {step === 1 && this.renderFirstStep()}
        {step === 2 && this.renderSecondStep()}
        {step === 3 && this.renderThirdStep()}
        {currentModal === MODAL_FOLDER_TREE &&
          this.renderMultiSelectFolderTreeModal()}
        {currentModal === MODAL_DEPARTMENT_TREE &&
          this.renderSelectDepartmentTreeModal()}
      </Fragment>
    );
  }
}

AlertModal.defaultProps = {
  alertId: null,
  step: 1,
  active: true,
  name: '',
  message: '',
  occurrenceInterval: '',
  occurrencePeriod: null,
  startDate: new Date(),
  occurrenceEndTimespan: null,
  occurrenceEndDate: null,
  occurrenceEndLimit: '',
  folders: [],
  filters: [],
  users: [],
  excludedUsers: [],
  userFields: [],
  departments: [],
  editAccess: false,
  isDuplicated: false,
};

AlertModal.propTypes = {
  classes: PropTypes.object.isRequired,
  handleAlertAction: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  alertId: PropTypes.number,
  step: PropTypes.number,
  active: PropTypes.bool,
  name: PropTypes.string,
  message: PropTypes.string,
  occurrenceInterval: PropTypes.string,
  occurrencePeriod: PropTypes.object,
  startDate: PropTypes.string,
  occurrenceEndTimespan: PropTypes.object,
  occurrenceEndDate: PropTypes.string,
  occurrenceEndLimit: PropTypes.string,
  folders: PropTypes.array,
  filters: PropTypes.array,
  users: PropTypes.array,
  excludedUsers: PropTypes.array,
  departments: PropTypes.array,
  editAccess: PropTypes.bool,
  isDuplicated: PropTypes.bool,
};

const mapStateToProps = ({ currentUser }) => ({ currentUser });

export default injectIntl(
  injectSheet(styles)(
    connect(mapStateToProps)(withFlags(injectIntl(withFormatDate(AlertModal)))),
  ),
);
